import { useEffect, useState, useMemo } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Home,
  Building,
  MessageSquare,
  Book,
  Users,
  DollarSign,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../components/data-table";
import { Badge } from "../components/ui/badge";

interface TrusteeStats {
  hostelsManaged: number;
  totalStudents: number;
  totalBeds: number;
  totalFunding: number;
  trusteeName: string;
}

interface ManagedHostel {
  id: string;
  name: string;
  city: string | null;
  status: string | null;
  total_capacity: number;
  occupied_count: number | null;
  funding_needed: number | null;
}

export default function TrusteeDashboard() {
  const { user, userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();
  const [stats, setStats] = useState<TrusteeStats>({
    hostelsManaged: 0,
    totalStudents: 0,
    totalBeds: 0,
    totalFunding: 0,
    trusteeName: "Trustee",
  });
  const [hostels, setHostels] = useState<ManagedHostel[]>([]);

  useEffect(() => {
    if (!loading && userRole && userRole !== "trustee") {
      navigate("/portal-selection");
    }
  }, [userRole, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        return;
      }

      try {
        // Fetch trustee profile
        const { data: trusteeData } = await supabase
          .from("trustees")
          .select("*")
          .eq("user_id", user.id);

        if (trusteeData && trusteeData.length > 0) {
          const trustee = trusteeData[0];

          // Fetch hostels managed by this trustee
          const hostelIds = trusteeData
            .filter((t: any) => t.hostel_id)
            .map((t: any) => t.hostel_id);

          let managedHostels: ManagedHostel[] = [];
          let totalStudents = 0;
          let totalBeds = 0;
          let totalFunding = 0;

          if (hostelIds.length > 0) {
            const { data: hostelsData } = await supabase
              .from("hostels")
              .select(
                "id, name, city, status, total_capacity, occupied_count, funding_needed"
              )
              .in("id", hostelIds);

            if (hostelsData) {
              managedHostels = hostelsData;
              totalBeds = hostelsData.reduce(
                (sum: number, h: any) => sum + (h.total_capacity ?? 0),
                0
              );
              totalStudents = hostelsData.reduce(
                (sum: number, h: any) => sum + (h.occupied_count ?? 0),
                0
              );
              totalFunding = hostelsData.reduce(
                (sum: number, h: any) => sum + (h.funding_needed ?? 0),
                0
              );
            }
          }

          setHostels(managedHostels);
          setStats({
            hostelsManaged: managedHostels.length,
            totalStudents,
            totalBeds,
            totalFunding,
            trusteeName: trustee.name || "Trustee",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [user]);

  const hostelsTableColumns = useMemo<TableColumnDef<ManagedHostel>[]>(
    () => [
      {
        id: "name",
        label: "Hostel Name",
        width: 180,
        renderCell: h => <span className="font-medium">{h.name}</span>,
      },
      {
        id: "city",
        label: "City",
        width: 120,
        renderCell: h => h.city ?? "N/A",
      },
      {
        id: "capacity",
        label: "Capacity",
        width: 100,
        renderCell: h => h.total_capacity,
      },
      {
        id: "occupied",
        label: "Occupied",
        width: 100,
        renderCell: h => h.occupied_count ?? 0,
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        renderCell: h => (
          <Badge
            variant={
              h.status === "approved"
                ? "default"
                : h.status === "rejected"
                  ? "destructive"
                  : "secondary"
            }
            className={h.status === "approved" ? "bg-green-600" : ""}
          >
            {h.status ?? "pending"}
          </Badge>
        ),
      },
      {
        id: "funding",
        label: "Funding Needed",
        width: 140,
        renderCell: h =>
          h.funding_needed ? `₹${h.funding_needed.toLocaleString()}` : "-",
      },
    ],
    []
  );

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/dashboard/trustee",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "My Hostels",
      path: "/trustee/institutions",
      icon: <Building className="h-5 w-5" />,
    },
    {
      label: "Scholarships",
      path: "/trustee/scholarships",
      icon: <Book className="h-5 w-5" />,
    },
    {
      label: "Funding",
      path: "/trustee/loans",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Resources",
      path: "/trustee/resources",
      icon: <Book className="h-5 w-5" />,
    },
    {
      label: "Help",
      path: "/trustee/help",
      icon: <MessageSquare className="h-5 w-5" />,
    },
  ];

  return (
    <DashboardLayout
      sidebarItems={sidebarItems}
      currentPath="/dashboard/trustee"
    >
      <div className="space-y-6">
        <div>
          <h2 className="mb-2 text-3xl font-bold text-foreground">
            Trustee Dashboard
          </h2>
          <p className="text-muted-foreground">
            Welcome, {stats.trusteeName}! Manage your responsibilities and
            oversight.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Hostels Managed
              </CardTitle>
              <Building className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {stats.hostelsManaged}
              </div>
              <p className="text-xs text-muted-foreground">
                Under your oversight
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-500/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Students
              </CardTitle>
              <Users className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.totalStudents}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently residing
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-500/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Bed Capacity
              </CardTitle>
              <Home className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.totalBeds}
              </div>
              <p className="text-xs text-muted-foreground">Available beds</p>
            </CardContent>
          </Card>

          <Card className="border-amber-500/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Funding Needed
              </CardTitle>
              <DollarSign className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                ₹{stats.totalFunding.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Across all hostels
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Managed Hostels */}
        <DataTableProvider
          tableId="trustee-dashboard-hostels"
          columns={hostelsTableColumns}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
              <CardTitle>Hostels Under Your Management</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/trustee/institutions")}
                >
                  View All
                </Button>
                <DataTableColumnSettings columns={hostelsTableColumns} />
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {hostels.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="mb-4 text-muted-foreground">
                    No hostels assigned yet
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contact admin to get hostels assigned to your account.
                  </p>
                </div>
              ) : (
                <DataTable
                  embedded
                  hideToolbar
                  tableId="trustee-dashboard-hostels"
                  columns={hostelsTableColumns}
                  data={hostels}
                  getRowKey={h => h.id}
                />
              )}
            </CardContent>
          </Card>
        </DataTableProvider>

        {/* Quick Actions */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate("/trustee/institutions")}
              >
                <Building className="mr-2 h-4 w-4" /> View All Hostels
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/trustee/scholarships")}
              >
                <Book className="mr-2 h-4 w-4" /> Manage Scholarships
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/trustee/loans")}
              >
                <DollarSign className="mr-2 h-4 w-4" /> Funding Requests
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Occupancy Overview</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.totalBeds > 0 ? (
                <div>
                  <div className="mb-2 flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Overall Occupancy
                    </span>
                    <span className="font-semibold">
                      {Math.round(
                        (stats.totalStudents / stats.totalBeds) * 100
                      )}
                      %
                    </span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-muted">
                    <div
                      className="h-3 rounded-full bg-primary transition-all"
                      style={{
                        width: `${Math.min(100, (stats.totalStudents / stats.totalBeds) * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-sm text-muted-foreground">
                    <span>{stats.totalStudents} occupied</span>
                    <span>{stats.totalBeds - stats.totalStudents} vacant</span>
                  </div>
                </div>
              ) : (
                <p className="py-4 text-center text-muted-foreground">
                  No data available
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
